import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProductivityDashboard } from './hooks/useProductivityDashboard'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import AppDateInput from '../../../components/AppDateInput'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import DashboardRightDrawer from '../../dashboard/shared/components/DashboardRightDrawer'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts'
import ActivityTimelineCard from '../../dashboard/personal/components/ActivityTimelineCard'
import TimesheetProgressCard from '../../dashboard/personal/components/TimesheetProgressCard'

const ACTIVITY_PAGE_SIZE = 5
const STACK_COLORS = ['#00327d', '#0f5ec7', '#2c7be5', '#5a95eb', '#85b0ef', '#aecbf5', '#d6e4fb']

const TREND_FALLBACK = {
  hourly: ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00'],
  daily: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  weekly: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'],
}

function initials(name) {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function logStatus(row) {
  const status = String(row?.status || '').toUpperCase()
  if (status === 'ACTIVE' || status === 'LIVE') return { label: 'Active', badge: 'bg-emerald-50 text-emerald-700' }
  if (status === 'FAILED') return { label: 'Failed', badge: 'bg-rose-50 text-rose-700' }
  return { label: 'Idle', badge: 'bg-amber-50 text-amber-700' }
}

function adjustmentStatusClass(status) {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'APPROVED') return 'bg-emerald-100 text-emerald-700'
  if (normalized === 'REJECTED') return 'bg-error-container text-error'
  return 'bg-primary-fixed text-primary'
}

function formatStartTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDetectedTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function formatDuration(seconds) {
  const total = Math.max(0, Number(seconds || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (hours > 0) return `${hours}h ${minutes}m ${secs}s`
  if (minutes > 0) return `${minutes}m ${secs}s`
  return `${secs}s`
}

function parseDurationLabelToSeconds(label) {
  const text = String(label || '').trim().toLowerCase()
  if (!text) return 0
  let hours = 0
  let minutes = 0
  const hMatch = text.match(/(\d+)\s*h/)
  const mMatch = text.match(/(\d+)\s*m/)
  if (hMatch) hours = Number(hMatch[1] || 0)
  if (mMatch) minutes = Number(mMatch[1] || 0)
  if (!Number.isFinite(hours)) hours = 0
  if (!Number.isFinite(minutes)) minutes = 0
  return Math.max(0, (hours * 3600) + (minutes * 60))
}

function formatDateTime(value) {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleString()
}

function normalizeReasonRows(rows) {
  const source = Array.isArray(rows) ? rows.filter((row) => Number(row?.percentage || 0) > 0) : []
  if (!source.length) {
    return []
  }
  return source.map((row) => ({
    label: row.label || 'Others',
    percentage: Number(row.percentage || 0),
    weeklyHours: Number(row.weeklyHours || 0),
    color: row.color || '#94a3b8',
  }))
}

function buildSmoothPath(points) {
  if (!points.length) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

  let path = `M ${points[0].x} ${points[0].y}`
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1]
    const curr = points[i]
    const cx = (prev.x + curr.x) / 2
    path += ` Q ${cx} ${prev.y}, ${curr.x} ${curr.y}`
  }
  return path
}

function buildAreaPath(points, chartHeight) {
  if (!points.length) return ''
  const line = buildSmoothPath(points)
  const last = points[points.length - 1]
  const first = points[0]
  return `${line} L ${last.x} ${chartHeight} L ${first.x} ${chartHeight} Z`
}

function normalizeTrendData(viewData, mode) {
  const fallbackLabels = TREND_FALLBACK[mode] || TREND_FALLBACK.hourly
  const labels = Array.isArray(viewData?.labels) && viewData.labels.length ? viewData.labels : fallbackLabels
  const currentHours = labels.map((_, index) => Number(viewData?.currentHours?.[index] || 0))
  const baselineHours = labels.map((_, index) => Number(viewData?.baselineHours?.[index] || 0))

  const values = [...currentHours, ...baselineHours]
  const maxValue = Math.max(0.1, ...values)

  return { labels, currentHours, baselineHours, maxValue }
}

function formatStackDuration(seconds) {
  const total = Math.max(0, Number(seconds || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const remSeconds = total % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes}m ${String(remSeconds).padStart(2, '0')}s`
  return `${remSeconds}s`
}

function buildUsageStackChartData(rows) {
  const safeRows = Array.isArray(rows) ? rows : []
  const totalShare = safeRows.reduce((sum, item) => sum + Number(item.sharePercent || 0), 0)
  const normalizedFactor = totalShare > 0 ? 100 / totalShare : 0
  const keys = safeRows.map((_, index) => `seg${index + 1}`)
  const dataRow = { label: 'usage' }
  safeRows.forEach((item, index) => {
    const share = Number(item.sharePercent || 0)
    dataRow[keys[index]] = Number((share * normalizedFactor).toFixed(2))
  })
  return { keys, data: [dataRow] }
}

function UsageStackTooltip({ active, payload, labelMap }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null
  const item = payload[0]
  const rawKey = String(item?.dataKey || '')
  const row = labelMap.get(rawKey)
  if (!row) return null
  return (
    <ChartTooltipSurface>
      <p className="text-sm font-bold text-on-surface">{row.name}</p>
      <p className="mt-1 text-xs text-on-surface-variant">Rank #{row.rank}</p>
      <p className="mt-1 text-xs text-on-surface-variant">Duration: {formatStackDuration(row.seconds)}</p>
      <p className="text-xs font-semibold text-primary">Share: {Number(row.sharePercent || 0).toFixed(2)}%</p>
    </ChartTooltipSurface>
  )
}

export default function ProductivityDashboardScreen() {
  const { state, actions } = useProductivityDashboard()
  const navigate = useNavigate()
  const [idleTrendView, setIdleTrendView] = useState('hourly')
  const drill = state.drill
  useBodyScrollLock(Boolean(drill?.open))

  useEffect(() => {
    const defaultView = state.summary?.idleTrendDefaultView || 'hourly'
    if (defaultView !== idleTrendView) {
      setIdleTrendView(defaultView)
    }
  }, [state.summary?.idleTrendDefaultView])

  const reasons = useMemo(() => normalizeReasonRows(state.summary.reasonBreakdown), [state.summary.reasonBreakdown])
  const donutStyle = useMemo(() => {
    if (!reasons.length) return { background: '#e0e3e5' }
    let cursor = 0
    const stops = reasons
      .filter((row) => Number(row.percentage || 0) > 0)
      .map((row) => {
        const start = cursor
        cursor += Math.max(0, Number(row.percentage || 0))
        return `${row.color} ${start}% ${Math.min(cursor, 100)}%`
      })
    if (cursor < 100) stops.push(`#e0e3e5 ${cursor}% 100%`)
    return { background: `conic-gradient(${stops.join(', ')})` }
  }, [reasons])

  const matrixBars = useMemo(
    () => (Array.isArray(state.summary.productiveOutputMatrix) ? state.summary.productiveOutputMatrix : []).slice(0, 7),
    [state.summary.productiveOutputMatrix],
  )
  const appActivityRows = useMemo(
    () => (Array.isArray(state.summary.appActivityRows) ? state.summary.appActivityRows : []),
    [state.summary.appActivityRows],
  )
  const appPage = Number(state.summary.appPage || 1)
  const appPageSize = Number(state.summary.appPageSize || ACTIVITY_PAGE_SIZE)
  const appTotal = Number(state.summary.appTotal || 0)
  const appTotalPages = Math.max(1, Math.ceil((appTotal || 0) / Math.max(1, appPageSize)))
  const appRangeStart = appTotal ? ((appPage - 1) * appPageSize) + 1 : 0
  const appRangeEnd = appTotal ? Math.min(appPage * appPageSize, appTotal) : 0

  const domainActivityRows = useMemo(
    () => (Array.isArray(state.summary.domainActivityRows) ? state.summary.domainActivityRows : []),
    [state.summary.domainActivityRows],
  )
  const domainPage = Number(state.summary.domainPage || 1)
  const domainPageSize = Number(state.summary.domainPageSize || ACTIVITY_PAGE_SIZE)
  const domainTotal = Number(state.summary.domainTotal || 0)
  const domainTotalPages = Math.max(1, Math.ceil((domainTotal || 0) / Math.max(1, domainPageSize)))
  const domainRangeStart = domainTotal ? ((domainPage - 1) * domainPageSize) + 1 : 0
  const domainRangeEnd = domainTotal ? Math.min(domainPage * domainPageSize, domainTotal) : 0
  const domainFilter = state.summary.domainFilter || 'all'

  const ambientRedDomainRows = useMemo(
    () => (Array.isArray(state.summary.ambientRedDomainRows) ? state.summary.ambientRedDomainRows : []),
    [state.summary.ambientRedDomainRows],
  )
  const ambientPage = Number(state.summary.ambientPage || 1)
  const ambientPageSize = Number(state.summary.ambientPageSize || ACTIVITY_PAGE_SIZE)
  const ambientTotal = Number(state.summary.ambientTotal || 0)
  const ambientTotalPages = Math.max(1, Math.ceil((ambientTotal || 0) / Math.max(1, ambientPageSize)))
  const ambientRangeStart = ambientTotal ? ((ambientPage - 1) * ambientPageSize) + 1 : 0
  const ambientRangeEnd = ambientTotal ? Math.min(ambientPage * ambientPageSize, ambientTotal) : 0

  const appUsageStackRows = useMemo(
    () => (Array.isArray(state.summary?.usageStacks?.appUsage) ? state.summary.usageStacks.appUsage : []),
    [state.summary?.usageStacks?.appUsage],
  )
  const redDomainUsageStackRows = useMemo(
    () => (Array.isArray(state.summary?.usageStacks?.redDomainUsage) ? state.summary.usageStacks.redDomainUsage : []),
    [state.summary?.usageStacks?.redDomainUsage],
  )
  const appUsageChart = useMemo(() => buildUsageStackChartData(appUsageStackRows), [appUsageStackRows])
  const redDomainUsageChart = useMemo(() => buildUsageStackChartData(redDomainUsageStackRows), [redDomainUsageStackRows])
  const appUsageLabelMap = useMemo(() => {
    const map = new Map()
    appUsageChart.keys.forEach((key, index) => map.set(key, appUsageStackRows[index]))
    return map
  }, [appUsageChart.keys, appUsageStackRows])
  const redDomainUsageLabelMap = useMemo(() => {
    const map = new Map()
    redDomainUsageChart.keys.forEach((key, index) => map.set(key, redDomainUsageStackRows[index]))
    return map
  }, [redDomainUsageChart.keys, redDomainUsageStackRows])

  const adjustmentsRows = useMemo(
    () => (Array.isArray(state.summary.adjustmentsRows) ? state.summary.adjustmentsRows : []),
    [state.summary.adjustmentsRows],
  )
  const adjustmentsPage = Number(state.summary.adjustmentsPage || 1)
  const adjustmentsPageSize = Number(state.summary.adjustmentsPageSize || ACTIVITY_PAGE_SIZE)
  const adjustmentsTotal = Number(state.summary.adjustmentsTotal || 0)
  const adjustmentsTotalPages = Math.max(1, Math.ceil((adjustmentsTotal || 0) / Math.max(1, adjustmentsPageSize)))
  const adjustmentsRangeStart = adjustmentsTotal ? ((adjustmentsPage - 1) * adjustmentsPageSize) + 1 : 0
  const adjustmentsRangeEnd = adjustmentsTotal ? Math.min(adjustmentsPage * adjustmentsPageSize, adjustmentsTotal) : 0

  const trendData = useMemo(() => {
    const viewData = state.summary?.idleTrendViews?.[idleTrendView] || {}
    return normalizeTrendData(viewData, idleTrendView)
  }, [state.summary?.idleTrendViews, idleTrendView])

  const companyDropdownOptions = useMemo(
    () => [
      { value: 'all', label: 'All Companies' },
      ...state.companyOptions.map((option) => ({ value: String(option.id), label: option.name })),
    ],
    [state.companyOptions],
  )

  const departmentDropdownOptions = useMemo(
    () => [
      { value: 'all', label: 'All Departments' },
      ...state.departmentOptions.map((option) => ({ value: String(option.id), label: option.name })),
    ],
    [state.departmentOptions],
  )

  const userDropdownOptions = useMemo(
    () => [
      { value: 'all', label: 'All Users' },
      ...state.userOptions.map((option) => ({ value: String(option.id), label: option.name })),
    ],
    [state.userOptions],
  )

  // ── KPI calculations ────────────────────────────────────────────
  const productivityRatio = Number(state.summary.productivityRatioPercent || 0)
  const productivityRatioSafe = Math.max(0, productivityRatio)
  const productivityRatioVisual = Math.min(productivityRatioSafe, 100)
  const gaugeStroke = (productivityRatioVisual / 100) * 251

  const trackedSeconds = parseDurationLabelToSeconds(state.summary.totalActiveDuration)
  const productiveSeconds = parseDurationLabelToSeconds(state.summary.productiveDuration)
  const idleSeconds = parseDurationLabelToSeconds(state.summary.totalIdle)
  const productiveShare = trackedSeconds > 0 ? (productiveSeconds / trackedSeconds) * 100 : 0
  const idleShare = trackedSeconds > 0 ? (idleSeconds / trackedSeconds) * 100 : 0

  const maxBar = Math.max(
    1,
    ...matrixBars.map((row) => Math.max(Number(row.productiveHours || row.activeHours || 0), Number(row.idleHours || 0))),
  )

  const chartWidth = 780
  const chartHeight = 220
  const chartPadding = 20
  const xStep = trendData.labels.length > 1 ? (chartWidth - (chartPadding * 2)) / (trendData.labels.length - 1) : 0
  const currentPoints = trendData.labels.map((_, index) => {
    const value = Number(trendData.currentHours[index] || 0)
    const yNorm = value / trendData.maxValue
    return {
      x: chartPadding + (xStep * index),
      y: chartHeight - chartPadding - (yNorm * (chartHeight - (chartPadding * 2))),
      value,
    }
  })
  const baselinePoints = trendData.labels.map((_, index) => {
    const value = Number(trendData.baselineHours[index] || 0)
    const yNorm = value / trendData.maxValue
    return {
      x: chartPadding + (xStep * index),
      y: chartHeight - chartPadding - (yNorm * (chartHeight - (chartPadding * 2))),
      value,
    }
  })

  const currentLinePath = buildSmoothPath(currentPoints)
  const baselineLinePath = buildSmoothPath(baselinePoints)
  const areaPath = buildAreaPath(currentPoints, chartHeight)

  return (
    <>
      <div className="mx-auto w-full max-w-[1500px] pb-10">
        <header className="mb-8 flex flex-wrap items-start justify-between gap-6">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-primary">Productivity Dashboard</h1>
            <p className="mt-1 text-sm font-medium text-on-surface-variant">Proctor Module v10.1 | Real-time Monitoring</p>
            {state.summary?.filters?.effectiveDateFrom || state.summary?.filters?.effectiveDateTo ? (
              <p className="mt-1 text-xs text-on-surface-variant">
                Data range: {state.summary.filters.effectiveDateFrom || '-'} to {state.summary.filters.effectiveDateTo || '-'}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => actions.openDrill('session')}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-on-primary hover:opacity-90"
          >
            <span className="material-symbols-outlined text-sm">open_in_new</span>
            View session details
          </button>
        </header>

        <section className="mb-8 grid grid-cols-1 gap-4 rounded-xl bg-surface-container-low p-6 md:grid-cols-2 xl:grid-cols-5">
          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Company</span>
            <AppDropdown
              value={state.companyId}
              onChange={(value) => actions.setCompanyId(String(value))}
              options={companyDropdownOptions}
              triggerClassName="rounded-lg border-none bg-surface-container-lowest py-2.5 text-sm font-medium shadow-none"
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Team / Dept</span>
            <AppDropdown
              value={state.departmentId}
              onChange={(value) => actions.setDepartmentId(String(value))}
              options={departmentDropdownOptions}
              triggerClassName="rounded-lg border-none bg-surface-container-lowest py-2.5 text-sm font-medium shadow-none"
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">User</span>
            <AppDropdown
              value={state.userId}
              onChange={(value) => actions.setUserId(String(value))}
              options={userDropdownOptions}
              triggerClassName="rounded-lg border-none bg-surface-container-lowest py-2.5 text-sm font-medium shadow-none"
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Date From</span>
            <AppDateInput
              value={state.dateFrom}
              onChange={actions.setDateFrom}
              inputClassName="rounded-lg border-none bg-surface-container-lowest py-2.5 text-sm font-medium shadow-none"
            />
          </label>

          <label className="grid gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Date To</span>
            <AppDateInput
              value={state.dateTo}
              onChange={actions.setDateTo}
              inputClassName="rounded-lg border-none bg-surface-container-lowest py-2.5 text-sm font-medium shadow-none"
            />
          </label>

        </section>

        {state.error ? (
          <div className="mb-6 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
        ) : null}

        {state.isLoading ? (
          <div className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
            Loading productivity dashboard...
          </div>
        ) : (
          <>
            {/* ── KPI Cards + Gauge ──────────────────────────────── */}
            <section className="mb-8 grid grid-cols-12 gap-6">
              <div className="col-span-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:col-span-8">

                <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Avg. Login Time</p>
                  <h3 className="font-headline text-3xl font-extrabold text-primary">{state.summary.avgLoginTime || '-'}</h3>
                  <button type="button" onClick={() => actions.openDrill('login_logout')} className="mt-3 text-xs font-bold text-primary hover:underline">View login details</button>
                </article>

                <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Avg. Logout Time</p>
                  <h3 className="font-headline text-3xl font-extrabold text-primary">{state.summary.avgLogoutTime || '-'}</h3>
                  <button type="button" onClick={() => actions.openDrill('login_logout')} className="mt-3 text-xs font-bold text-primary hover:underline">View logout details</button>
                </article>

                {/* Tracked Active Time — bar shows active÷capacity */}
                <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Tracked Active Time</p>
                  <h3 className="font-headline text-3xl font-extrabold text-primary">{state.summary.totalActiveDuration}</h3>
                  <button type="button" onClick={() => actions.openDrill('tracked_time')} className="mt-3 text-xs font-bold text-primary hover:underline">View tracked sessions</button>
                </article>

                {/* Productive — bar shows productive÷active */}
                <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Productive</p>
                  <h3 className="font-headline text-3xl font-extrabold text-emerald-700">{state.summary.productiveDuration}</h3>
                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                    <span className="block h-full rounded-full bg-emerald-600" style={{ width: `${Math.min(100, Math.max(0, productiveShare)).toFixed(2)}%` }} />
                  </div>
                  <p className="mt-2 text-[10px] text-on-surface-variant">{Math.max(0, productiveShare).toFixed(1)}% of tracked time</p>
                  <button type="button" onClick={() => actions.openDrill('productive_time')} className="mt-3 text-xs font-bold text-primary hover:underline">View productive sessions</button>
                </article>

                {/* Idle Time — bar shows idle÷active */}
                <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                  <p className="mb-1 text-xs font-bold uppercase tracking-wider text-on-surface-variant">Idle Time</p>
                  <h3 className="font-headline text-3xl font-extrabold text-amber-600">{state.summary.totalIdle}</h3>
                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                    <span className="block h-full rounded-full bg-amber-500" style={{ width: `${Math.min(100, Math.max(0, idleShare)).toFixed(2)}%` }} />
                  </div>
                  <p className="mt-2 text-[10px] text-on-surface-variant">{Math.max(0, idleShare).toFixed(1)}% of tracked time</p>
                  <button type="button" onClick={() => actions.openDrill('idle_time')} className="mt-3 text-xs font-bold text-primary hover:underline">View idle sessions</button>
                </article>

                {/* Approved Productive Hours — Productivity Adjustments approval */}
                <article className="flex flex-col justify-between rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                  <div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Approved Productive Hours</p>
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                        Approved
                      </span>
                    </div>
                    <h3 className="mt-1 font-headline text-3xl font-extrabold text-primary">
                      {state.summary.approvedAdjustmentDuration || state.summary.approvedProductiveHours || state.summary.productiveAdjustedDuration || '0h 00m'}
                    </h3>

                    <div className="mt-3 grid grid-cols-2 gap-2 border-t border-outline-variant/15 pt-2.5">
                      <button
                        type="button"
                        onClick={() => actions.openDrill('adjustment', { status: 'PENDING' })}
                        className="group flex flex-col rounded p-1 text-left transition hover:bg-surface-container-low"
                        title="View Pending Adjustments"
                      >
                        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-on-surface-variant">
                          <span className="h-2 w-2 rounded-full bg-amber-500" />
                          Pending
                        </span>
                        <span className="mt-0.5 text-sm font-extrabold text-amber-700 group-hover:underline">
                          {state.summary.pendingAdjustmentDuration || state.summary.pendingProductiveHours || '0h 00m'}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => actions.openDrill('adjustment', { status: 'REJECTED' })}
                        className="group flex flex-col rounded p-1 text-left transition hover:bg-surface-container-low"
                        title="View Rejected Adjustments"
                      >
                        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-on-surface-variant">
                          <span className="h-2 w-2 rounded-full bg-rose-500" />
                          Rejected
                        </span>
                        <span className="mt-0.5 text-sm font-extrabold text-rose-700 group-hover:underline">
                          {state.summary.rejectedAdjustmentDuration || state.summary.rejectedProductiveHours || '0h 00m'}
                        </span>
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => actions.openDrill('adjustment', { status: 'APPROVED' })}
                    className="mt-3 text-xs font-bold text-primary hover:underline"
                  >
                    View productivity adjustments
                  </button>
                </article>

              </div>

              {/* Goal Achievement Gauge — productive÷capacity */}
              <article className="col-span-12 flex flex-col items-center justify-center overflow-hidden rounded-xl bg-primary p-8 text-on-primary lg:col-span-4">
                <h3 className="mb-6 text-lg font-bold">Utilization Gauge</h3>
                <div className="relative h-48 w-48">
                  <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="transparent" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      stroke="white"
                      strokeWidth="8"
                      strokeDasharray={`${gaugeStroke} 251`}
                    />
                  </svg>
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="text-center">
                      <p className="text-4xl font-extrabold">{productivityRatioSafe.toFixed(1)}%</p>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-primary-fixed-dim">Productivity Ratio</p>
                    </div>
                  </div>
                </div>
                <p className="mt-6 text-center text-sm leading-relaxed text-primary-fixed">
                  Productive time out of expected productive hours in selected filters and date range.
                </p>
                <p className="mt-2 text-xs text-primary-fixed-dim">Productive {state.summary.productiveDuration} | Capacity {state.summary.capacityDuration}</p>
                <button type="button" onClick={() => actions.openDrill('utilization')} className="mt-4 text-xs font-bold text-primary-fixed hover:underline">View employee capacity details</button>
              </article>
            </section>

            {/* ── Activity Daily Timeline & Timesheet Progress ─────────────────── */}
            <section className="mb-8 grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-8">
                <ActivityTimelineCard
                  shiftStartTime={state.summary.dailyPulse?.shiftStartTime}
                  shiftEndTime={state.summary.dailyPulse?.shiftEndTime}
                  firstLogin={state.summary.dailyPulse?.firstLogin}
                  totalIdle={state.summary.dailyPulse?.totalIdle}
                  longestStreak={state.summary.dailyPulse?.longestStreak}
                  totalBreak={state.summary.dailyPulse?.totalBreak}
                  shiftTiming={state.summary.dailyPulse?.shiftTiming}
                  timelineSegments={state.summary.dailyPulse?.timelineSegments}
                />
              </div>
              <div className="col-span-12 lg:col-span-4">
                <TimesheetProgressCard
                  score={state.summary.timesheetProgress?.score}
                  goalLabel={state.summary.timesheetProgress?.goalLabel}
                  goalValue={state.summary.timesheetProgress?.goalValue}
                  progress={state.summary.timesheetProgress?.progress}
                />
              </div>
            </section>

            {/* ── Logout by Reason + Idle Trend ─────────────────── */}
            <section className="mb-8 grid grid-cols-12 gap-6">
              <article className="col-span-12 rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm md:col-span-5">
                <h3 className="mb-6 font-headline text-xl font-bold text-primary">Logout by Reason</h3>
                {reasons.length ? (
                  <div className="flex items-center gap-8">
                    <div className="relative h-32 w-32 rounded-full" style={donutStyle}>
                      <span className="absolute inset-[14px] rounded-full bg-surface-container-lowest" />
                    </div>
                    <div className="flex-1 space-y-3">
                      {reasons.map((row) => (
                        <button key={row.label} type="button" onClick={() => actions.openDrill('logout_reason', { reason: row.label })} className="flex w-full items-center justify-between rounded px-1 text-left text-sm hover:bg-surface-container-low focus:outline-none focus:ring-2 focus:ring-primary">
                          <span className="inline-flex items-center gap-2 font-medium text-on-surface">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: row.color }} />
                            {row.label}
                          </span>
                          <span className="font-bold text-on-surface-variant">{Math.round(row.percentage)}%</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="grid h-32 place-items-center text-sm text-on-surface-variant">No logout reason data in selected range.</div>
                )}
              </article>

              <article className="col-span-12 rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm md:col-span-7">
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">Idle Trend Analysis</span>
                    <h3 className="font-headline text-2xl font-bold text-on-surface">Performance Stability</h3>
                  </div>
                  <div className="flex gap-3 rounded-md bg-surface-container px-3 py-1.5">
                    {['hourly', 'daily', 'weekly'].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setIdleTrendView(mode)}
                        className={`text-xs font-bold ${idleTrendView === mode ? 'text-primary' : 'text-on-surface-variant/60'}`}
                      >
                        {mode[0].toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative h-64 w-full">
                  <svg className="h-full w-full" viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
                    <defs>
                      <linearGradient id="idleTrendGradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#00327d" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#00327d" stopOpacity="0" />
                      </linearGradient>
                    </defs>

                    <line x1="0" y1="55" x2={chartWidth} y2="55" stroke="#e0e3e5" strokeDasharray="4" />
                    <line x1="0" y1="110" x2={chartWidth} y2="110" stroke="#e0e3e5" strokeDasharray="4" />
                    <line x1="0" y1="165" x2={chartWidth} y2="165" stroke="#e0e3e5" strokeDasharray="4" />

                    {areaPath ? <path d={areaPath} fill="url(#idleTrendGradient)" /> : null}
                    {currentLinePath ? <path d={currentLinePath} fill="none" stroke="#00327d" strokeLinecap="round" strokeWidth="3" /> : null}
                    {baselineLinePath ? <path d={baselineLinePath} fill="none" stroke="#57657a" strokeDasharray="6" strokeOpacity="0.45" strokeWidth="1.5" /> : null}

                    {currentPoints.map((point, index) => (
                      <AppTooltip
                        key={`current-${trendData.labels[index]}`}
                        content={`${trendData.labels[index]} Idle ${point.value.toFixed(2)}h`}
                      >
                        <circle cx={point.x} cy={point.y} r="4" fill="#00327d" className="cursor-pointer" role="button" tabIndex="0" aria-label={`View ${trendData.labels[index]} stability details`} onClick={() => actions.openDrill('stability')} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); actions.openDrill('stability') } }} />
                      </AppTooltip>
                    ))}
                  </svg>

                  <div className="absolute bottom-2 left-0 right-0 flex justify-around px-4">
                    {trendData.labels.map((label) => (
                      <span key={`label-${label}`} className="text-[10px] font-bold uppercase text-on-surface-variant">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            </section>

            {/* ── Productive Output Matrix ──────────────────────── */}
            <section className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-lowest p-7">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h3 className="text-4xl font-extrabold tracking-tight text-on-surface">Productive Output Matrix</h3>
                  <p className="mt-1 text-base text-on-surface-variant">Cross-departmental engagement over selected range.</p>
                </div>
                <div className="inline-flex items-center gap-4 text-sm font-bold text-on-surface-variant">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-3 w-3 rounded-full bg-primary" />
                    PRODUCTIVE
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="h-3 w-3 rounded-full bg-surface-container-highest" />
                    IDLE
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-7 items-end gap-3 rounded-2xl bg-surface-container-low p-4">
                {matrixBars.length ? (
                  matrixBars.map((bar) => {
                    const productive = Number(bar.productiveHours || bar.activeHours || 0)
                    const totalSeconds = Number(bar.totalSeconds || 0)
                    const productiveHeight = Math.max(8, Math.round((productive / maxBar) * 220))
                    const idleHeight = Math.max(8, Math.round((Number(bar.idleHours || 0) / maxBar) * 220))
                    return (
                      <div key={bar.day} className="flex flex-col items-center gap-2">
                        <div className="flex h-[230px] w-full items-end justify-center gap-2">
                          <AppTooltip content={`Productive ${formatDuration(Number(bar.productiveSeconds || 0))} â€” view details`}>
                            <button type="button" aria-label={`View productive output for ${bar.day}`} onClick={() => actions.openDrill('output_matrix')} className="w-12 rounded-t-md bg-primary focus:outline-none focus:ring-2 focus:ring-primary" style={{ height: `${productiveHeight}px` }} />
                          </AppTooltip>
                          <AppTooltip content={`Idle ${formatDuration(Number(bar.idleSeconds || 0))} â€” view details`}>
                            <button type="button" aria-label={`View idle output for ${bar.day}`} onClick={() => actions.openDrill('output_matrix')} className="w-12 rounded-t-md bg-surface-container-highest focus:outline-none focus:ring-2 focus:ring-primary" style={{ height: `${idleHeight}px` }} />
                          </AppTooltip>
                        </div>
                        <p className="text-xs font-black uppercase tracking-[0.13em] text-on-surface-variant">{bar.day}</p>
                        <p className="text-[11px] font-bold text-on-surface-variant">{formatDuration(totalSeconds)}</p>
                      </div>
                    )
                  })
                ) : (
                  <div className="col-span-7 py-10 text-center text-sm text-on-surface-variant">No cumulative session data for selected filters.</div>
                )}
              </div>
            </section>

            {/* ── App Usage + Red Domain Stacks ────────────────── */}
            <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
              <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h3 className="font-headline text-xl font-bold text-primary">Application Usage</h3>
                    <p className="text-xs text-on-surface-variant">Stacked share by app name for selected filters</p>
                  </div>
                  <span className="material-symbols-outlined text-primary/60">apps</span>
                </div>
                <div className="h-28">
                  {appUsageChart.keys.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={appUsageChart.data}
                        layout="vertical"
                        margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                      >
                        <XAxis type="number" hide domain={[0, 100]} />
                        <YAxis type="category" dataKey="label" hide />
                        <RechartsTooltip
                          cursor={false}
                          shared={false}
                          content={<UsageStackTooltip labelMap={appUsageLabelMap} />}
                          animationDuration={180}
                        />
                        {appUsageChart.keys.map((key, index) => (
                          <Bar key={key} dataKey={key} stackId="app-usage" radius={index === appUsageChart.keys.length - 1 ? [0, 999, 999, 0] : index === 0 ? [999, 0, 0, 999] : 0} isAnimationActive animationDuration={500}>
                            <Cell fill={STACK_COLORS[index % STACK_COLORS.length]} className="cursor-pointer" onClick={() => actions.openDrill('application', { app_name: key })} />
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-on-surface-variant">No application usage in selected range.</div>
                  )}
                </div>
              </article>

              <article className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h3 className="font-headline text-xl font-bold text-primary">Red Domains Usage</h3>
                    <p className="text-xs text-on-surface-variant">Stacked share by red-domain name for selected filters</p>
                  </div>
                  <span className="material-symbols-outlined text-primary/60">block</span>
                </div>
                <div className="h-28">
                  {redDomainUsageChart.keys.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={redDomainUsageChart.data}
                        layout="vertical"
                        margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                      >
                        <XAxis type="number" hide domain={[0, 100]} />
                        <YAxis type="category" dataKey="label" hide />
                        <RechartsTooltip
                          cursor={false}
                          shared={false}
                          content={<UsageStackTooltip labelMap={redDomainUsageLabelMap} />}
                          animationDuration={180}
                        />
                        {redDomainUsageChart.keys.map((key, index) => (
                          <Bar key={key} dataKey={key} stackId="red-domain-usage" radius={index === redDomainUsageChart.keys.length - 1 ? [0, 999, 999, 0] : index === 0 ? [999, 0, 0, 999] : 0} isAnimationActive animationDuration={500}>
                            <Cell fill={STACK_COLORS[index % STACK_COLORS.length]} className="cursor-pointer" onClick={() => actions.openDrill('domain', { domain: key })} />
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="grid h-full place-items-center text-sm text-on-surface-variant">No red-domain usage in selected range.</div>
                  )}
                </div>
              </article>
            </section>

            {/* ── Application Activity Table ───────────────────── */}
            <section className="mt-8 rounded-xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm overflow-hidden">
              <div className="flex items-center justify-between border-b border-outline-variant/10 px-6 py-4">
                <div>
                  <h3 className="font-headline font-bold text-primary">Application Usage</h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">Session-level application activity for selected range</p>
                </div>
                <span className="material-symbols-outlined text-primary/60">desktop_windows</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-surface-container-low">
                      <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">User Account</th>
                      <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Application</th>
                      <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Start Time</th>
                      <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Duration</th>
                      <th className="px-6 py-4 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {state.summary?.appLoading ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center">
                          <div className="flex h-12 items-center justify-center">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                          </div>
                        </td>
                      </tr>
                    ) : appActivityRows.length ? appActivityRows.map((row, index) => {
                      const status = logStatus(row)
                      const avatarUrl = resolveMediaUrl(row.profile_photo_path)
                      const rowKey = `${row.user_id}-${row.session_id || 's'}-${row.app_name}-${row.start_time || index}`
                      return (
                        <tr key={rowKey} className="group transition-colors hover:bg-surface-container-low">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={row.user_name || 'User'}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-primary">
                                  {initials(row.user_name)}
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-semibold">{row.user_name || '-'}</p>
                                <p className="text-[10px] text-slate-400">{row.department_name || '-'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-sm text-slate-400">desktop_windows</span>
                              <button type="button" onClick={() => actions.openDrill('application', { app_name: row.app_name })} className="text-sm font-medium text-primary hover:underline">{row.app_name || '-'}</button>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">{formatStartTime(row.start_time)}</td>
                          <td className="px-6 py-4 text-sm font-medium">{formatDuration(row.duration_seconds)}</td>
                          <td className="px-6 py-4">
                            <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-tight ${status.badge}`}>
                              {status.label}
                            </span>
                          </td>
                        </tr>
                      )
                    }) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center text-sm text-on-surface-variant">
                          No application activity logged in selected range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {appTotal > 0 ? (
                <div className="flex flex-col gap-3 border-t border-outline-variant/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-xs text-on-surface-variant">
                    Showing {appRangeStart} to {appRangeEnd} of {appTotal} rows
                  </span>
                  <AppPagination
                    page={appPage}
                    totalPages={appTotalPages}
                    onPageChange={(p) => actions.setAppPage(Number(p))}
                    onPrevious={() => actions.setAppPage(Math.max(1, appPage - 1))}
                    onNext={() => actions.setAppPage(Math.min(appTotalPages, appPage + 1))}
                    canPrevious={appPage > 1}
                    canNext={appPage < appTotalPages}
                  />
                </div>
              ) : null}
            </section>

            {/* ── Browser Domain Activity ───────────────────────── */}
            <section className="mt-6 overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
              <div className="flex flex-col gap-3 border-b border-outline-variant/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-headline font-bold text-primary">Browser Domain Activity</h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">Domains visited in selected range - red domains are flagged</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => actions.setDomainFilter('all')}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${domainFilter === 'all'
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'
                      }`}
                  >
                    All Domains
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.setDomainFilter('red')}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${domainFilter === 'red'
                      ? 'bg-red-600 text-white shadow-sm'
                      : 'bg-red-50 text-red-700 hover:bg-red-100 ring-1 ring-red-200'
                      }`}
                  >
                    <span className="material-symbols-outlined text-xs">block</span>
                    Red Domains Only
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                {state.summary?.domainLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : domainActivityRows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-on-surface-variant">
                    {domainFilter === 'red' ? 'No red domain activity found in selected range.' : 'No domain data in selected range.'}
                  </p>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-surface-container-low">
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">User Account</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Domain</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Browser</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Time Spent</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Classification</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      {domainActivityRows.map((row, index) => {
                        const hours = Math.floor(row.total_seconds / 3600)
                        const mins = Math.floor((row.total_seconds % 3600) / 60)
                        const secs = row.total_seconds % 60
                        const timeLabel = hours > 0
                          ? `${hours}h ${mins}m`
                          : mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
                        const isRed = row.classification === 'red'
                        const avatarUrl = resolveMediaUrl(row.profile_photo_path)
                        return (
                          <tr key={`${row.user_id || 'u'}-${row.domain}-${index}`} className="group transition-colors hover:bg-surface-container-low">
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-3">
                                {avatarUrl ? (
                                  <img
                                    src={avatarUrl}
                                    alt={row.user_name || 'User'}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-primary">
                                    {initials(row.user_name)}
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-semibold">{row.user_name || '-'}</p>
                                  <p className="text-[10px] text-slate-400">{row.department_name || '-'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-2">
                                {isRed ? (
                                  <AppTooltip content="Red domain - flagged as non-productive">
                                    <span className="material-symbols-outlined text-sm leading-none text-red-500">lens</span>
                                  </AppTooltip>
                                ) : null}
                                <button type="button" onClick={() => actions.openDrill('domain', { domain: row.domain })} className="text-sm font-semibold text-primary hover:underline">{row.domain}</button>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-xs text-on-surface-variant">{row.browser_name || '—'}</td>
                            <td className="px-6 py-3 text-sm font-medium text-on-surface">{timeLabel}</td>
                            <td className="px-6 py-3">
                              {isRed ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase text-red-700">
                                  <span className="material-symbols-outlined text-xs">block</span>
                                  Red Domain
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-surface-container px-2.5 py-1 text-[10px] font-bold uppercase text-on-surface-variant">
                                  General
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {domainTotal > 0 ? (
                <div className="flex flex-col gap-3 border-t border-outline-variant/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-xs text-on-surface-variant">
                    Showing {domainRangeStart} to {domainRangeEnd} of {domainTotal} rows
                  </span>
                  <AppPagination
                    page={domainPage}
                    totalPages={domainTotalPages}
                    onPageChange={(p) => actions.setDomainPage(Number(p))}
                    onPrevious={() => actions.setDomainPage(Math.max(1, domainPage - 1))}
                    onNext={() => actions.setDomainPage(Math.min(domainTotalPages, domainPage + 1))}
                    canPrevious={domainPage > 1}
                    canNext={domainPage < domainTotalPages}
                  />
                </div>
              ) : null}
            </section>

            {/* ── Ambient Red Domains (Background Detection) ────── */}
            {/* {ambientRedDomainRows.length > 0 || ambientTotal > 0 ? (
            <section className="mt-6 overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
              <div className="flex items-center justify-between border-b border-outline-variant/10 px-6 py-4">
                <div>
                  <h3 className="font-headline font-bold text-primary flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary/60">visibility</span>
                    Ambient Red Domains
                  </h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">
                    Prohibited domains open in background (split-screen / second monitor) — scanned every 60s (1 detection = ~1 min visible); not focused, but actively open.
                  </p>
                </div>
                <span className="material-symbols-outlined text-primary/60">radar</span>
              </div>
              <div className="overflow-x-auto">
                {state.summary?.ambientLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-surface-container-low">
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">User Account</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Domain</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Detections</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">First Seen</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Last Seen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      {ambientRedDomainRows.map((row, index) => {
                        const avatarUrl = resolveMediaUrl(row.profile_photo_path)
                        return (
                          <tr key={`${row.user_id || 'u'}-${row.domain}-ambient-${index}`} className="group transition-colors hover:bg-surface-container-low">
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-3">
                                {avatarUrl ? (
                                  <img src={avatarUrl} alt={row.user_name || 'User'} className="h-8 w-8 rounded-full object-cover" />
                                ) : (
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-primary">
                                    {initials(row.user_name)}
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-semibold">{row.user_name || '—'}</p>
                                  <p className="text-[10px] text-slate-400">{row.department_name || '—'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm leading-none text-red-500">lens</span>
                                <button type="button" onClick={() => actions.openDrill('domain', { domain: row.domain })} className="text-sm font-semibold text-red-700 hover:underline">{row.domain}</button>
                              </div>
                            </td>
                            <td className="px-6 py-3">
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-700 ring-1 ring-red-200">
                                <span className="material-symbols-outlined text-[11px]">notifications_active</span>
                                {row.total_detections ?? '—'}x
                              </span>
                            </td>
                            <td className="px-6 py-3 text-xs text-on-surface-variant">{formatDetectedTime(row.first_detected_at)}</td>
                            <td className="px-6 py-3 text-xs text-on-surface-variant">{formatDetectedTime(row.last_detected_at)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {ambientTotal > ambientPageSize ? (
                <div className="flex flex-col gap-3 border-t border-outline-variant/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-xs text-on-surface-variant">
                    Showing {ambientRangeStart} to {ambientRangeEnd} of {ambientTotal} rows
                  </span>
                  <AppPagination
                    page={ambientPage}
                    totalPages={ambientTotalPages}
                    onPageChange={(p) => actions.setAmbientPage(Number(p))}
                    onPrevious={() => actions.setAmbientPage(Math.max(1, ambientPage - 1))}
                    onNext={() => actions.setAmbientPage(Math.min(ambientTotalPages, ambientPage + 1))}
                    canPrevious={ambientPage > 1}
                    canNext={ambientPage < ambientTotalPages}
                  />
                </div>
              ) : null}
            </section>
          ) : null} */}

            {/* ── Session Productivity Adjustments ─────────────── */}
            <section className="mt-6 overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
              <div className="flex items-center justify-between border-b border-outline-variant/10 px-6 py-4">
                <div>
                  <h3 className="font-headline font-bold text-primary">Session Productivity Adjustments</h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">Manual productive-time adjustments for selected filters</p>
                </div>
                <span className="material-symbols-outlined text-primary/60">tune</span>
              </div>
              {state.summary.adjustmentActionError ? (
                <div className="mx-6 mt-4 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-xs text-error">
                  {state.summary.adjustmentActionError}
                </div>
              ) : null}
              {state.summary.adjustmentActionMessage ? (
                <div className="mx-6 mt-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
                  {state.summary.adjustmentActionMessage}
                </div>
              ) : null}
              <div className="overflow-x-auto">
                {state.adjustmentsLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : adjustmentsRows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-on-surface-variant">No productivity adjustments in selected range.</p>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-surface-container-low">
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">User</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Department</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Reason</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Added Productive Time</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Remark</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Status</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Decision Remark</th>
                        <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Logged At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      {adjustmentsRows.map((row, index) => (
                        <tr key={`${row.id || 'adj'}-${index}`} className="group transition-colors hover:bg-surface-container-low">
                          <td className="px-6 py-3 text-sm font-semibold text-on-surface">{row.userName || '-'}</td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant">{row.departmentName || '-'}</td>
                          <td className="px-6 py-3 text-xs font-medium text-on-surface"><button type="button" onClick={() => actions.openDrill('adjustment')} className="text-left hover:text-primary hover:underline">{row.reasonLabel || row.reasonCode || '-'}</button></td>
                          <td className="px-6 py-3 text-sm font-semibold text-primary">{row.manualActiveLabel || formatDuration(row.manualActiveSeconds)}</td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant">{row.remark || '-'}</td>
                          <td className="px-6 py-3">
                            <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${adjustmentStatusClass(row.approvalStatus)}`}>
                              {String(row.approvalStatus || 'PENDING').toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant">{row.decisionRemark || '-'}</td>
                          <td className="px-6 py-3 text-xs text-on-surface-variant">{formatDateTime(row.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {adjustmentsTotal > 0 ? (
                <div className="flex flex-col gap-3 border-t border-outline-variant/10 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-xs text-on-surface-variant">
                    Showing {adjustmentsRangeStart} to {adjustmentsRangeEnd} of {adjustmentsTotal} rows
                  </span>
                  <AppPagination
                    page={adjustmentsPage}
                    totalPages={adjustmentsTotalPages}
                    onPageChange={(value) => actions.setAdjustmentsPage(Number(value))}
                    onPrevious={() => actions.setAdjustmentsPage(Math.max(1, adjustmentsPage - 1))}
                    onNext={() => actions.setAdjustmentsPage(Math.min(adjustmentsTotalPages, adjustmentsPage + 1))}
                    canPrevious={adjustmentsPage > 1}
                    canNext={adjustmentsPage < adjustmentsTotalPages}
                  />
                </div>
              ) : null}
            </section>
          </>
        )}
      </div>
      <DashboardRightDrawer
        open={Boolean(drill?.open)}
        title={drill?.title || 'Productivity details'}
        subtitle={(drill?.breadcrumb || []).join(' › ')}
        onClose={actions.closeDrill}
        page={Number(drill?.page || 1)}
        totalPages={Math.max(1, Math.ceil(Number(drill?.total || 0) / Math.max(1, Number(drill?.pageSize || 25))))}
        onPrevious={() => actions.setDrillPage(Math.max(1, Number(drill?.page || 1) - 1))}
        onNext={() => actions.setDrillPage(Number(drill?.page || 1) + 1)}
        onPageChange={actions.setDrillPage}
        headerExtra={
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={actions.closeDrill} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Back</button>
              <button type="button" onClick={actions.closeDrill} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Up one level</button>
              <button type="button" onClick={actions.resetDrill} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Reset drilldown</button>
              <button type="button" onClick={() => actions.exportDrill('csv')} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Export CSV</button>
              <button type="button" onClick={() => actions.exportDrill('pdf')} className="rounded-lg border border-outline-variant/30 px-3 py-1.5 text-xs font-bold text-primary">Export PDF</button>
            </div>
            <p className="text-xs text-on-surface-variant">Current company, department, user and date filters are retained. {drill?.calculationBasis || ''}</p>
          </div>
        }
      >
        {drill?.loading ? <p className="py-10 text-center text-sm text-on-surface-variant">Loading details…</p> : null}
        {drill?.error ? <p className="rounded-lg bg-error-container p-3 text-sm text-error">{drill.error}</p> : null}
        {!drill?.loading && !drill?.error && !(drill?.rows || []).length ? <p className="py-10 text-center text-sm text-on-surface-variant">No records match the selected criteria.</p> : null}
        {!drill?.loading && !drill?.error && (drill?.rows || []).length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-surface-container-low">
                <tr>{(drill.columns || []).map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2 font-bold text-on-surface-variant">{column.label}</th>)}{drill.rows.some((row) => row.timesheetId) ? <th className="px-3 py-2 font-bold text-on-surface-variant">Source</th> : null}</tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/15">
                {drill.rows.map((row, index) => <tr key={row.timesheetId || row.sessionId || `${row.userId || 'row'}-${index}`}><>{(drill.columns || []).map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2 text-on-surface">{column.format === 'duration' ? formatDuration(row[column.key]) : column.format === 'hours' ? `${Number(row[column.key] || 0).toFixed(2)}h` : (row[column.key] ?? '—')}</td>)}{row.timesheetId ? <td className="px-3 py-2"><button type="button" onClick={() => navigate(`/nerve/timesheet/entry?id=${row.timesheetId}&mode=view`)} className="font-bold text-primary hover:underline">View timesheet</button></td> : null}</></tr>)}
              </tbody>
            </table>
          </div>
        ) : null}
      </DashboardRightDrawer>
    </>
  )
}
